// Ask Your Data sagas.
//
// Calls the backend /signal/ask, /signal/ask/sessions endpoints. Every
// request includes the standard auth + workspace headers via
// getLocalUser(); the backend uses X-Workspace-Id to scope reads/writes
// and to enforce workspace isolation.

import { call, put, take, takeLatest, cancelled } from 'redux-saga/effects';
import { eventChannel, END, buffers } from 'redux-saga';
import axios from 'axios';

import config from '../../../config';
import { getLocalUser } from '../../../utils/localUser';
import { Error as GlobalError } from '../../actions/error/error';

import {
  ASK_REQUEST,
  SESSIONS_REQUEST,
  SESSION_LOAD_REQUEST,
  SESSION_DELETE_REQUEST,
  STREAM_REQUEST,

  askSuccess, askFailure,
  fetchSessionsSuccess, fetchSessionsFailure,
  loadSessionSuccess, loadSessionFailure,
  deleteSessionSuccess, deleteSessionFailure,

  streamStart, streamTextDelta, streamToolStart, streamToolEnd,
  streamAttachment, streamDone, streamError,
} from '../../actions/signals/askData';


function* askTurn(action) {
  try {
    const { session_id, question } = action.payload || {};
    const response = yield call(
      axios.post,
      `${config.API_URL}/signal/ask`,
      { session_id, question },
      { headers: getLocalUser() },
    );
    const data = response?.data?.data || response?.data || {};
    yield put(askSuccess(data));
  } catch (error) {
    const status  = error?.response?.status;
    const payload = error?.response?.data?.data || error?.response?.data || {};
    // Canonical envelope: {error: {code, message, details}, status}.
    const apiErr  = typeof payload.error === 'object' ? payload.error : null;
    if (status === 402) {
      yield put(askFailure({
        kind: 'insufficient_credits',
        message: apiErr?.message || payload.message || payload.status || 'Insufficient credits',
        balance: apiErr?.details?.balance ?? payload.balance,
      }));
      return;
    }
    yield put(GlobalError(error));
    yield put(askFailure({
      kind: 'error',
      message: apiErr?.message || payload.message || payload.status || error?.message || 'Failed to send question',
    }));
  }
}


function* fetchSessions() {
  try {
    const response = yield call(
      axios.get,
      `${config.API_URL}/signal/ask/sessions`,
      { headers: getLocalUser() },
    );
    const data = response?.data?.data || response?.data || {};
    yield put(fetchSessionsSuccess(data.sessions || []));
  } catch (error) {
    yield put(GlobalError(error));
    yield put(fetchSessionsFailure(error?.message || 'Failed to load sessions'));
  }
}


function* loadSession(action) {
  try {
    const { session_id } = action.payload || {};
    const response = yield call(
      axios.get,
      `${config.API_URL}/signal/ask/sessions/${encodeURIComponent(session_id)}`,
      { headers: getLocalUser() },
    );
    const data = response?.data?.data || response?.data || {};
    yield put(loadSessionSuccess(data.session || null));
  } catch (error) {
    yield put(GlobalError(error));
    yield put(loadSessionFailure(error?.message || 'Failed to load session'));
  }
}


function* deleteSession(action) {
  try {
    const { session_id } = action.payload || {};
    yield call(
      axios.delete,
      `${config.API_URL}/signal/ask/sessions/${encodeURIComponent(session_id)}`,
      { headers: getLocalUser() },
    );
    yield put(deleteSessionSuccess(session_id));
  } catch (error) {
    yield put(GlobalError(error));
    yield put(deleteSessionFailure(error?.message || 'Failed to delete session'));
  }
}


// ---------------------------------------------------------------------------
// Streaming saga (SSE over fetch + ReadableStream)
// ---------------------------------------------------------------------------
//
// We can't use EventSource because it doesn't support custom headers
// (and we need Authorization + X-Workspace-Id). Instead we POST via
// fetch, treat response.body as a ReadableStream, and parse SSE
// frames by hand.

const SSE_DELIMITER = /\r?\n\r?\n/;


// Build a redux-saga eventChannel that emits one redux action per
// parsed SSE event. The channel closes (END) when the stream ends
// or errors.
// CRITICAL: eventChannel needs an expanding buffer. The default
// (no buffer) silently drops events when the saga's `take(channel)`
// loop isn't waiting at the exact instant `emit()` is called - and
// that happens constantly with SSE because a single network read
// can deliver multiple `data:` frames, which we then emit() back-
// to-back faster than the saga can re-enter `take()`. Without a
// buffer, every chunk after the first in such a burst is lost,
// which manifests as missing chain-of-thought blocks (tool_start
// / tool_end / text_delta events go silently into the void) even
// though the server sent them all.
function buildSseChannel(url, init) {
  return eventChannel((emit) => {
    let aborted = false;
    const controller = new AbortController();
    init.signal = controller.signal;

    (async () => {
      try {
        const response = await fetch(url, init);
        if (!response.ok) {
          // Try to surface 402 / 4xx error bodies cleanly.
          let parsed = null;
          try {
            const txt = await response.text();
            parsed = JSON.parse(txt);
          } catch (_e) { /* ignore */ }
          const data = parsed?.data || parsed || {};
          // Canonical envelope: {error: {code, message, details}, status}.
          const apiErr = typeof data.error === 'object' ? data.error : null;
          if (response.status === 402) {
            emit(streamError({
              kind:    'insufficient_credits',
              message: apiErr?.message || data.message || data.status || 'Insufficient credits',
              balance: apiErr?.details?.balance ?? data.balance,
            }));
          } else {
            emit(streamError({
              kind:    'http_error',
              message: data.message || data.error || `Request failed (${response.status})`,
            }));
          }
          emit(END);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        while (true) {
          if (aborted) break;
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          // Split on blank-line boundaries; each chunk is one SSE event.
          let parts = buffer.split(SSE_DELIMITER);
          buffer = parts.pop();   // last piece may be partial; keep it
          for (const part of parts) {
            const dataLines = part
              .split(/\r?\n/)
              .filter((l) => l.startsWith('data:'))
              .map((l) => l.slice(5).trim());
            if (!dataLines.length) continue;
            const raw = dataLines.join('\n');
            let payload;
            try { payload = JSON.parse(raw); } catch (_e) { continue; }
            const ev = payload?.event;
            switch (ev) {
              case 'start':
                emit(streamStart({
                  session_id: payload.session_id,
                  title:      payload.title,
                }));
                break;
              case 'text_delta':
                emit(streamTextDelta(payload.text || ''));
                break;
              case 'tool_start':
                emit(streamToolStart({
                  tool_call_id: payload.tool_call_id,
                  name:         payload.name,
                  label:        payload.label,
                }));
                break;
              case 'tool_end':
                emit(streamToolEnd({
                  tool_call_id: payload.tool_call_id,
                  name:         payload.name,
                  ok:           payload.ok,
                  error:        payload.error,
                  row_count:    payload.row_count,
                  spec:         payload.spec,
                }));
                break;
              case 'attachment':
                emit(streamAttachment({
                  kind: payload.kind,
                  spec: payload.spec,
                }));
                break;
              case 'warning':
                emit({
                  type: 'SIGNAL_ASK_DATA_STREAM_WARNING',
                  payload: {
                    kind:    payload.kind,
                    message: payload.message,
                  },
                });
                break;
              case 'done':
                emit(streamDone({
                  session_id:  payload.session_id,
                  title:       payload.title,
                  envelope:    payload.envelope,
                  usage:       payload.usage,
                  sources:     payload.sources,
                  new_balance: payload.new_balance,
                }));
                break;
              case 'error':
                emit(streamError({
                  kind:    payload.kind || 'error',
                  message: payload.message || 'Error',
                  balance: payload.balance,
                }));
                break;
              default:
                break;
            }
          }
        }
      } catch (err) {
        if (!aborted) {
          emit(streamError({
            kind:    'network',
            message: err?.message || 'Connection failed',
          }));
        }
      } finally {
        emit(END);
      }
    })();

    return () => {
      aborted = true;
      try { controller.abort(); } catch (_e) { /* ignore */ }
    };
  }, buffers.expanding(64));
}


// Upload one composer attachment to the auto-typing import endpoint.
// Returns the meta the ask body needs ({filename, execution_id, ...});
// the backend waits for the import before the model runs.
function* uploadAttachment(file) {
  const form = new FormData();
  form.append('file', file, file.name);
  const response = yield call(
    axios.post,
    `${config.API_URL}/ingestion/connect/upload`,
    form,
    { headers: getLocalUser() },
  );
  const data = response?.data?.data || response?.data || {};
  const columns = Array.isArray(data.detected_schema?.columns)
    ? data.detected_schema.columns.map((c) => c?.name).filter(Boolean)
    : [];
  return {
    filename:     data.filename || file.name,
    execution_id: data.execution_id,
    size_bytes:   data.file_size_bytes ?? file.size,
    columns,
  };
}


function* askStream(action) {
  const { session_id, question, files, kickoff } = action.payload || {};

  // Stage the attached CSVs first - each upload returns an
  // execution_id the backend awaits before answering.
  const attachments = [];
  if (Array.isArray(files) && files.length) {
    for (const file of files) {
      try {
        attachments.push(yield call(uploadAttachment, file));
      } catch (error) {
        const payload = error?.response?.data?.data || error?.response?.data || {};
        const apiMsg = typeof payload.error === 'object' ? payload.error?.message : null;
        yield put(streamError({
          kind:    'upload_failed',
          message: `Couldn't upload ${file.name}: ${apiMsg || payload.message || payload.status || error?.message || 'upload failed'}`,
        }));
        return;
      }
    }
  }

  const headers = {
    ...getLocalUser(),
    'Accept':       'text/event-stream',
    'Content-Type': 'application/json',
  };
  const channel = yield call(
    buildSseChannel,
    `${config.API_URL}/signal/ask/stream`,
    {
      method:  'POST',
      headers,
      body:    JSON.stringify({
        session_id,
        question,
        ...(attachments.length ? { attachments } : {}),
        // App-initiated turn (workspace setup): the backend persists the
        // user turn hidden and uses a default instruction when empty.
        ...(kickoff ? { kickoff: true } : {}),
        // Current route: lets "this goal" / "this page" resolve when the
        // question comes from the docked chat on a goal (or other) page.
        page: window.location.pathname,
      }),
    },
  );
  try {
    while (true) {
      const ev = yield take(channel);
      yield put(ev);
    }
  } finally {
    if (yield cancelled()) {
      try { channel.close(); } catch (_e) { /* ignore */ }
    }
  }
}


export function* watch() {
  yield takeLatest(ASK_REQUEST,            askTurn);
  yield takeLatest(SESSIONS_REQUEST,       fetchSessions);
  yield takeLatest(SESSION_LOAD_REQUEST,   loadSession);
  yield takeLatest(SESSION_DELETE_REQUEST, deleteSession);
  yield takeLatest(STREAM_REQUEST,         askStream);
}
